import { Box, Group, useMantineTheme } from '@mantine/core';
import { IconStar, IconStarFilled, IconStarHalfFilled } from '@tabler/icons-react';
import { forwardRef, useState } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface RatingProps {
  value?: number;
  defaultValue?: number;
  count?: number;
  size?: number;
  readOnly?: boolean;
  allowHalf?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
  onChange?: (value: number) => void;
}

const Rating = forwardRef<HTMLDivElement, RatingProps>(
  (
    {
      value: controlledValue,
      defaultValue = 0,
      count = 5,
      size = 20,
      readOnly = false,
      allowHalf = false,
      gradientVariant = 'warning',
      useColorScheme = true,
      onChange,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    const [internalValue, setInternalValue] = useState(defaultValue);
    const [hoverValue, setHoverValue] = useState<number | null>(null);

    const value = controlledValue !== undefined ? controlledValue : internalValue;
    const displayValue = hoverValue !== null ? hoverValue : value;

    const handleClick = (starValue: number) => {
      if (readOnly) return;
      const newValue = controlledValue === undefined ? starValue : undefined;
      if (newValue !== undefined) setInternalValue(newValue);
      onChange?.(starValue);
    };

    const handleMouseMove = (e: React.MouseEvent, starIndex: number) => {
      if (readOnly) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const isHalf = allowHalf && x < rect.width / 2;
      setHoverValue(isHalf ? starIndex + 0.5 : starIndex + 1);
    };

    const handleMouseLeave = () => {
      if (!readOnly) setHoverValue(null);
    };

    const renderStar = (index: number) => {
      const starValue = index + 1;
      const isFilled = displayValue >= starValue;
      const isHalf = !isFilled && displayValue >= starValue - 0.5;

      const StarIcon = isFilled ? IconStarFilled : isHalf ? IconStarHalfFilled : IconStar;

      return (
        <Box
          key={index}
          component="button"
          type="button"
          onClick={() => handleClick(allowHalf && isHalf ? starValue - 0.5 : starValue)}
          onMouseMove={(e) => handleMouseMove(e, index)}
          onMouseLeave={handleMouseLeave}
          aria-label={`Rate ${starValue} star${starValue !== 1 ? 's' : ''}`}
          aria-pressed={displayValue >= starValue}
          style={{
            background: 'none',
            border: 'none',
            padding: 2,
            cursor: readOnly ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '4px',
          }}
          disabled={readOnly}
        >
          <StarIcon
            size={size}
            style={{
              color: isFilled || isHalf ? color : theme.colors.gray[4],
              transition: reducedMotion ? 'none' : 'color 0.15s ease',
            }}
            aria-hidden="true"
          />
        </Box>
      );
    };

    return (
      <Group ref={ref} gap={2} role="radiogroup" aria-label={`Rating out of ${count} stars`}>
        {Array.from({ length: count }).map((_, i) => renderStar(i))}
      </Group>
    );
  }
);

Rating.displayName = 'Rating';

export default Rating;
